const ML_WORKFLOW = "PATIENT_UPLOAD";
const BILLING_UNIT_TYPE = "IMAGE";
const ML_API_WORKFLOW = "patient-upload";
const API_BILLING_UNIT_TYPE = "patient-image";
const MAX_UNITS = 150;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const GUID = /^(?:[A-Za-z0-9]{32}|c[A-Za-z0-9_-]{19,31})$/;
const MODEL_ORDER = [
  "Normal-Dr-NonDr",
  "Dr-Other",
  "Normal-Dr-NonDr-Pipeline-2",
];
const SUPPORTED_MODELS = new Set(MODEL_ORDER);

const deepFreeze = (value) => {
  if (!value || typeof value !== "object" || Object.isFrozen(value))
    return value;
  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
};

const normalizeLaterality = (value) =>
  ({
    OD: "right",
    R: "right",
    right: "right",
    OS: "left",
    L: "left",
    left: "left",
  }[value] || value);

const imageExtension = (file) => {
  const type = file && file.type;
  if (typeof type !== "string" || !/^image\/[A-Za-z0-9.+-]+$/.test(type)) {
    throw new Error("invalid patient image");
  }
  return type.split("/")[1];
};

const configuredModels = (models) => {
  const values = Array.isArray(models)
    ? models
    : typeof models === "string"
    ? models
        .split(",")
        .map((model) => model.trim())
        .filter(Boolean)
    : [];
  if (
    values.length < 1 ||
    new Set(values).size !== values.length ||
    values.some((model) => !SUPPORTED_MODELS.has(model))
  ) {
    throw new Error("invalid patient reservation models");
  }
  return MODEL_ORDER.filter((model) => values.includes(model));
};

export const createPatientReservationPlan = ({ images, models, s3Bucket } = {}) => {
  if (
    !Array.isArray(images) ||
    images.length < 1 ||
    images.length > MAX_UNITS ||
    typeof s3Bucket !== "string" ||
    s3Bucket.length < 1 ||
    s3Bucket.length > 63 ||
    !/^[\x21-\x7e]+$/.test(s3Bucket)
  ) {
    throw new Error("invalid patient reservation plan");
  }
  const orderedModels = configuredModels(models);
  const identities = new Set();
  const units = images.map((image, index) => {
    const file = image && image.file;
    const guid = image && image.fileData && image.fileData.guid;
    const laterality = normalizeLaterality(
      image && image.fileData && image.fileData.laterality
    );
    if (
      !GUID.test(guid || "") ||
      !["left", "right"].includes(laterality) ||
      !Number.isSafeInteger(file && file.size) ||
      file.size < 1 ||
      file.size > MAX_IMAGE_BYTES
    ) {
      throw new Error("invalid patient reservation image");
    }
    const s3Key = `original_${guid}.${imageExtension(file)}`;
    const members = orderedModels.map((model, modelIndex) => ({
          memberSetSequence: 1,
          workSequence: modelIndex + 1,
          model,
        }));
    const canonicalUnitIdentity = JSON.stringify({
      workflow: ML_WORKFLOW,
      billingUnitType: BILLING_UNIT_TYPE,
      guid,
      laterality,
      s3Bucket,
      s3Key,
      members,
    });
    if (identities.has(canonicalUnitIdentity)) {
      throw new Error("duplicate patient reservation image");
    }
    identities.add(canonicalUnitIdentity);
    return {
      unitSequence: index + 1,
      guid,
      laterality,
      s3Bucket,
      s3Key,
      members,
      canonicalUnitIdentity,
    };
  });
  return deepFreeze({
    workflow: ML_WORKFLOW,
    billingUnitType: BILLING_UNIT_TYPE,
    apiRequest: {
      workflow: ML_API_WORKFLOW,
      billingUnitType: API_BILLING_UNIT_TYPE,
      units: units.map(({ canonicalUnitIdentity }) => ({
        canonicalUnitIdentity,
      })),
    },
    units,
  });
};

export const applyPatientReservationPermissions = ({
  images,
  admission,
} = {}) => {
  if (!Array.isArray(images) || !admission || !Array.isArray(admission.units)) {
    throw new Error("invalid patient reservation mapping");
  }
  const permissionByIdentity = new Map();
  admission.units.forEach((unit) => {
    const planUnit = unit && unit.planUnit;
    const memberSets = unit && unit.memberSets;
    if (!planUnit || !Array.isArray(memberSets) || memberSets.length !== 1) {
      throw new Error("invalid patient reservation mapping");
    }
    const permission = memberSets[0] && memberSets[0].permission;
    if (
      !permission ||
      permission.unitSequence !== planUnit.unitSequence ||
      permission.memberSetSequence !== 1 ||
      permission.guid !== planUnit.guid ||
      permission.laterality !== planUnit.laterality ||
      permission.s3Bucket !== planUnit.s3Bucket ||
      permission.s3Key !== planUnit.s3Key
    ) {
      throw new Error("invalid patient reservation permission");
    }
    permissionByIdentity.set(`${planUnit.guid}:${planUnit.laterality}`, {
      permission,
      models: planUnit.members.map((member) => member.model).filter(Boolean),
    });
  });
  if (permissionByIdentity.size !== images.length) {
    throw new Error("patient reservation permission coverage mismatch");
  }
  return images.map((image) => {
    const guid = image.fileData.guid;
    const laterality = normalizeLaterality(image.fileData.laterality);
    const mapped = permissionByIdentity.get(`${guid}:${laterality}`);
    if (!mapped) throw new Error("patient reservation image identity mismatch");
    permissionByIdentity.delete(`${guid}:${laterality}`);
    return {
      ...image,
      reservationPermission: mapped.permission,
      fileData: {
        ...image.fileData,
        laterality,
        reservationPermissionId: mapped.permission.permissionId,
        reservationBatchId: mapped.permission.reservationId,
        reservationUnitSequence: mapped.permission.unitSequence,
        reservationMemberSetSequence: mapped.permission.memberSetSequence,
        reservationWorkflow: ML_WORKFLOW,
        reservationExpectedMemberSetCount: 1,
        reservationModels: mapped.models,
        s3Bucket: mapped.permission.s3Bucket,
        s3Key: mapped.permission.s3Key,
        relPath: mapped.permission.s3Key,
      },
    };
  });
};
